import styled, { css } from 'styled-components';
import { baseVariables } from '../../styles';
import { OCardStandardContentAlignment, OCardStandardPadding } from './OCardStandard.types';

const { color } = baseVariables;

const CARD_BOX_SHADOW = '0 0.25rem 1.25rem 0 rgba(0, 0, 0, 0.07)';

interface WrapperProps {
  $contentAlignment: OCardStandardContentAlignment;
  $padding: OCardStandardPadding;
  $isClickable: boolean;
}

interface ContentWrapperProps {
  $contentAlignment: OCardStandardContentAlignment;
  $padding: OCardStandardPadding;
}

interface MediaWrapperProps {
  $padding: OCardStandardPadding;
}

interface ButtonsWrapperProps {
  $contentAlignment: OCardStandardContentAlignment;
}

interface EyebrowWrapperProps {
  $contentAlignment: OCardStandardContentAlignment;
}

const multilineClamp = (mobileLines: number, tabletDesktopLines: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${mobileLines};
  line-clamp: ${mobileLines};
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${baseVariables.mediaQuery.md} {
    -webkit-line-clamp: ${tabletDesktopLines};
    line-clamp: ${tabletDesktopLines};
  }
`;

const singleLineClip = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`;

export const Wrapper = styled.article<WrapperProps>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;

  border-radius: var(--t-cards-border-radius, 14px);
  overflow: hidden;
  background-color: var(--t-brand-bg-color);
  ${({ $padding }) =>
    $padding !== OCardStandardPadding.None &&
    css`
      box-shadow: ${CARD_BOX_SHADOW};
    `}
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  ${({ $isClickable }) =>
    $isClickable &&
    css`
      &:focus-visible {
        outline: 2px solid var(--t-focus-outline-color, currentColor);
        outline-offset: 2px;
      }
    `}

  ${({ $padding }) =>
    $padding === OCardStandardPadding.EntireCard &&
    css`
      padding: 1rem;
    `}
`;

export const MediaWrapper = styled.div<MediaWrapperProps>`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: ${({ $padding }) =>
    $padding === OCardStandardPadding.EntireCard ? 'var(--t-images-border-radius-inset, 8px)' : '0'};
`;

export const ContentWrapper = styled.div<ContentWrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $contentAlignment }) =>
    $contentAlignment === OCardStandardContentAlignment.Centered ? 'center' : 'flex-start'};
  text-align: ${({ $contentAlignment }) =>
    $contentAlignment === OCardStandardContentAlignment.Centered ? 'center' : 'start'};
  padding-block: 1.5rem;

  ${({ $padding }) =>
    $padding === OCardStandardPadding.EntireCard &&
    css`
      padding-block-start: 1.5rem; /* 24px top, same as other modes */
      padding-block-end: 0.5rem; /* 8px bottom, less than other modes */
      padding-inline: 0.25rem; /* 4px left/right, only in EntireCard */
    `}
  ${({ $padding }) =>
    $padding === OCardStandardPadding.ContentOnly &&
    css`
      padding-inline: 1.5rem;
    `}
`;

export const EyebrowWrapper = styled.div<EyebrowWrapperProps>`
  width: 100%;
  display: flex;
  justify-content: ${({ $contentAlignment }) =>
    $contentAlignment === OCardStandardContentAlignment.Centered ? 'center' : 'flex-start'};
  ${singleLineClip}
`;

export const SubheaderWrapper = styled.div`
  width: 100%;
  ${multilineClamp(3, 2)}
`;

export const AccentLineWrapper = styled.div`
  width: 100%;
`;

export const BodyWrapper = styled.div`
  width: 100%;
  ${multilineClamp(3, 2)}
`;

export const ButtonsWrapper = styled.div<ButtonsWrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  padding-block-start: 1rem;
  align-items: ${({ $contentAlignment }) =>
    $contentAlignment === OCardStandardContentAlignment.Centered ? 'center' : 'stretch'};

  @media ${baseVariables.mediaQuery.md} {
    flex-direction: row;
    gap: 1rem;
    justify-content: ${({ $contentAlignment }) =>
      $contentAlignment === OCardStandardContentAlignment.Centered ? 'center' : 'flex-start'};
  }
`;

export const ButtonWrapper = styled.div`
  flex: 0 0 auto;
`;

export const TagWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-block: 0.5rem;
  ${singleLineClip}

  /*
   * In-card tag override (MDS-1611). MTag is wrapped with the legacy
   * 'm-badge-inline' class per dev-guide guidance ("Use m-badge-inline 
   * class for tags until global CSS is updated.").
   */
  .m-badge-inline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    height: 1.375rem;
    padding: 0 0.5rem;
    border-radius: 0.25rem;
    width: auto;

    background-color: ${color.neutral10};
    color: ${color.base10};

    text-transform: none;
    letter-spacing: normal;
    text-align: start;

    > span {
      color: ${color.base10};
      text-transform: none;
      letter-spacing: normal;
    }
  }
`;
